/**
 * Authentication state managed via Jotai.
 *
 * Stores:
 * - JWT access token (for API requests)
 * - User roles
 * - User ID
 * - User email
 *
 * Tokens are persisted in localStorage.
 * The axios client automatically refreshes tokens on 401 errors.
 */
import axios from "axios";
import { atom } from "jotai/vanilla";
import type { WritableAtom, SetStateAction } from "jotai";
import { jotaiStore } from "./jotaiStore";
import {
  MfaClaim,
  OrgRole,
  PageScope,
  UserRole,
  OrgFeature,
} from "../utils/types";
import settings from "../settings";
import {
  tokenFromStorage,
  uidFromStorage,
  rolesFromStorage,
  scopesFromStorage,
  emailFromStorage,
  orgRoleFromStorage,
  featuresFromStorage,
  orgIdFromStorage,
  persistToken,
  persistUid,
  persistRoles,
  persistScopes,
  persistEmail,
  persistOrgRole,
  persistOrgId,
  persistFeatures,
  extractScopes,
  extractOrgRole,
  extractOrgId,
  extractFeatures,
} from "../utils/authStorage";

type WritablePrimitive<Value> = WritableAtom<
  Value,
  [SetStateAction<Value>],
  void
>;

const createWritableAtom = <Value>(initialValue: Value) =>
  atom(initialValue) as WritablePrimitive<Value>;

export const tokenAtom = createWritableAtom<string | null>(tokenFromStorage);
export const rolesAtom = createWritableAtom<UserRole[] | null>(
  rolesFromStorage || null,
);
export const uidAtom = createWritableAtom<string | null>(uidFromStorage);
export const emailAtom = createWritableAtom<string | null>(emailFromStorage);
export const scopesAtom = createWritableAtom<PageScope[] | null>(
  scopesFromStorage || null,
);
export const orgRoleAtom = createWritableAtom<OrgRole | null>(
  orgRoleFromStorage,
);
export const orgIdAtom = createWritableAtom<number | null>(orgIdFromStorage);
export const featuresAtom = createWritableAtom<OrgFeature[] | null>(
  featuresFromStorage || null,
);
export const mfaClaimAtom = createWritableAtom<MfaClaim | null>(null);

/**
 * Tracks whether auth has completed its initial state check.
 */
export const authInitializedAtom = createWritableAtom<boolean>(true);

export const isAuthenticatedAtom = atom((get) => get(tokenAtom) !== null);

/**
 * Refreshes the access token using the refresh token.
 *
 * @throws Error if refresh fails and user must reconnect
 */
export const refreshToken = async (): Promise<string | null> => {
  // Single-flight: if refresh is already in progress, return the same promise
  if (refreshPromise !== null) {
    return refreshPromise;
  }

  const refreshTokenValue = localStorage.getItem("refresh_token");
  if (!refreshTokenValue) {
    await clearAuthState();
    return null;
  }

  // Create refresh promise and cache it
  refreshPromise = (async (): Promise<string | null> => {
    try {
      const baseURL =
        settings.baseUrl && settings.baseUrl.trim().length > 0
          ? settings.baseUrl.replace(/\/+$/, "")
          : "/api";

      const currentAccessToken = localStorage.getItem("token");
      const preferredOrgId = orgIdFromStorage;

      // Use a clean axios instance to avoid circular dependencies and interceptors
      const response = await axios.post(
        `${baseURL}/auth/refresh`,
        {
          refresh_token: refreshTokenValue,
          ...(preferredOrgId != null ? { org_id: preferredOrgId } : {}),
        },
        {
          withCredentials: true,
          headers: {
            // Pass current access token (even if expired) so the server can
            // preserve active org_id when body org_id is absent.
            ...(currentAccessToken
              ? { Authorization: `Bearer ${currentAccessToken}` }
              : { Authorization: undefined }),
          },
        },
      );

      const newAccessToken = response.data.access_token;

      if (!newAccessToken) {
        throw new Error("No access token in refresh response");
      }

      persistToken(newAccessToken);

      // Extract email + scopes from new token
      let email: string | null = null;
      let nextScopes: PageScope[] | null;
      let nextFeatures: OrgFeature[] | null;
      try {
        const payload = JSON.parse(
          atob(newAccessToken.split(".")[1]),
        ) as Record<string, unknown>;
        email = (payload.email as string) || null;
        if (email) {
          persistEmail(email);
        }
        nextScopes = extractScopes(payload);
        persistScopes(nextScopes);
        nextFeatures = extractFeatures(payload);
        persistFeatures(nextFeatures);
        const nextOrgRole = extractOrgRole(payload);
        persistOrgRole(nextOrgRole);
        const nextOrgId = extractOrgId(payload);
        persistOrgId(nextOrgId);
      } catch (error) {
        // If we can't decode, keep existing values from storage
        email = emailFromStorage;
        nextScopes = scopesFromStorage;
        nextFeatures = featuresFromStorage;
      }

      setAuthState({
        token: newAccessToken,
        roles: rolesFromStorage,
        scopes: nextScopes,
        features: nextFeatures,
        uid: uidFromStorage,
        email: email || emailFromStorage,
      });
      try {
        const payload = JSON.parse(
          atob(newAccessToken.split(".")[1]),
        ) as Record<string, unknown>;
        jotaiStore.set(orgRoleAtom, extractOrgRole(payload));
        jotaiStore.set(orgIdAtom, extractOrgId(payload));
      } catch {
        /* keep existing */
      }

      return newAccessToken;
    } catch (error) {
      console.error("Failed to refresh token:", error);
      // If refresh fails, user must reconnect - clear auth state
      await clearAuthState();
      throw error;
    } finally {
      // Clear the promise cache so future refreshes can proceed
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

const setAuthState = ({
  token,
  roles,
  scopes,
  features,
  uid,
  email,
}: {
  token: string | null;
  roles: UserRole[] | null;
  scopes: PageScope[] | null;
  features: OrgFeature[] | null;
  uid: string | null;
  email: string | null;
}) => {
  jotaiStore.set(tokenAtom, token);
  jotaiStore.set(rolesAtom, roles);
  jotaiStore.set(scopesAtom, scopes);
  jotaiStore.set(featuresAtom, features);
  jotaiStore.set(uidAtom, uid);
  jotaiStore.set(emailAtom, email);
};

/**
 * Clears the auth state and localStorage.
 * Used by the API client when token refresh fails to trigger a redirect to login.
 */
export const clearAuthState = async () => {
  // Clear any in-flight refresh promise
  refreshPromise = null;

  persistToken(null);
  persistUid(null);
  persistRoles(null);
  persistScopes(null);
  persistFeatures(null);
  persistEmail(null);
  persistOrgRole(null);
  persistOrgId(null);
  localStorage.removeItem("refresh_token");
  setAuthState({
    token: null,
    roles: null,
    scopes: null,
    features: null,
    uid: null,
    email: null,
  });
  jotaiStore.set(orgRoleAtom, null);
  jotaiStore.set(orgIdAtom, null);
};

/**
 * Promise cache for single-flight token refresh.
 * If multiple calls to refreshToken() happen concurrently, they all wait for the same refresh.
 */
let refreshPromise: Promise<string | null> | null = null;
